import { forwardRef, useState, type HTMLAttributes } from 'react';
import { cn } from '../../../utils';
import { Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';

export interface CodeBlockProps extends HTMLAttributes<HTMLDivElement> {
  /** The code content */
  code: string;
  /** Programming language for syntax highlighting hint */
  language?: string;
  /** Optional filename to display */
  filename?: string;
  /** Whether the code block is collapsible */
  collapsible?: boolean;
  /** Initial collapsed state */
  defaultCollapsed?: boolean;
  /** Maximum height before scrolling */
  maxHeight?: number | string;
  /** Show line numbers */
  showLineNumbers?: boolean;
  /** Highlight specific lines (1-indexed) */
  highlightLines?: number[];
}

export const CodeBlock = forwardRef<HTMLDivElement, CodeBlockProps>(
  (
    {
      className,
      code,
      language,
      filename,
      collapsible = true,
      defaultCollapsed = true,
      maxHeight = 400,
      showLineNumbers = false,
      highlightLines = [],
      ...props
    },
    ref
  ) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
    const [copied, setCopied] = useState(false);

    const lines = code.split('\n');
    const lineCount = lines.length;

    const handleCopy = async () => {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <div
        ref={ref}
        className={cn('mt-2', className)}
        {...props}
      >
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          {filename ? (
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400 font-mono">
              {filename}
            </span>
          ) : language ? (
            <span className="text-sm text-slate-500 dark:text-slate-400 uppercase">
              {language}
            </span>
          ) : null}
          <div className="flex items-center gap-1 ml-auto">
            {collapsible && (
              <button
                type="button"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                aria-label={isCollapsed ? 'Expand code' : 'Collapse code'}
              >
                {isCollapsed ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronUp className="h-3 w-3" />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={handleCopy}
              className="p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              aria-label="Copy code"
            >
              {copied ? (
                <Check className="h-3 w-3 text-emerald-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>

        {/* Code content */}
        {!isCollapsed && (
          <div
            className={cn(
              'rounded-lg overflow-hidden border',
              'bg-slate-50 border-slate-200 dark:bg-slate-800/50 dark:border-slate-700'
            )}
          >
            <div
              className="overflow-auto"
              style={{ maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }}
            >
              <pre className="p-3 leading-relaxed">
                <code className="font-mono text-sm">
                  {showLineNumbers ? (
                    <table className="w-full border-collapse">
                      <tbody>
                        {lines.map((line, i) => (
                          <tr
                            key={i}
                            className={cn(
                              highlightLines.includes(i + 1) &&
                                'bg-amber-100 dark:bg-amber-500/10 border-l-2 border-amber-400'
                            )}
                          >
                            <td className="pr-3 text-right text-slate-400 dark:text-slate-500 select-none w-8 align-top text-sm">
                              {i + 1}
                            </td>
                            <td className="pl-3 whitespace-pre text-sm text-slate-700 dark:text-slate-300">
                              {line || ' '}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <span className="whitespace-pre text-sm text-slate-700 dark:text-slate-300">
                      {code}
                    </span>
                  )}
                </code>
              </pre>
            </div>
          </div>
        )}

        {/* Collapsed state */}
        {isCollapsed && (
          <div className="text-sm text-slate-400 dark:text-slate-500">
            <span className="font-mono">{lineCount} lines</span>
            <span className="mx-2">·</span>
            <button
              type="button"
              onClick={() => setIsCollapsed(false)}
              className="text-sky-500 hover:text-sky-600 dark:hover:text-sky-400"
            >
              Show code
            </button>
          </div>
        )}
      </div>
    );
  }
);
CodeBlock.displayName = 'CodeBlock';
